import React from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';
import {
  BodyText,
  MeteredComponent,
  OnOffComponent,
  PrepaidComponent,
  PrepaidTranslations,
  QuantityComponent,
  SubscriptionComponentCardVariants,
} from '@chargify/custom-ui-components';

import {
  ComponentModel,
  MeteredComponent as MeteredComponentType,
  QuantityComponent as QuantityComponentType,
  PrepaidComponent as PrepaidComponentType,
  OnOffComponent as OnOffComponentType,
} from '../../models/component/types';
import { componentSelectors } from '../../machines';
import { ComponentTypes } from '../../machines/components';
import { SubscriptionComponent } from '../../components/SubscriptionComponents/types';

const renderComponent = (
  type: ComponentTypes,
  data: ComponentModel,
  cardVariant: SubscriptionComponentCardVariants,
  translations: {
    prepaid: PrepaidTranslations;
  }
) => {
  switch (type) {
    case 'metered':
      const meteredData = data as MeteredComponentType;
      return (
        <MeteredComponent
          name={meteredData.name}
          value={meteredData.unit.balance + ' ' + data.unit.name}
          description={meteredData.description}
        />
      );
    case 'quantity':
      const quantityData = data as QuantityComponentType;
      return (
        <QuantityComponent
          name={quantityData.name}
          value={quantityData.allocatedQuantity + ' ' + quantityData.unit.name}
          description={quantityData.description}
        />
      );
    case 'prepaid':
      const prepaidData = data as PrepaidComponentType;
      return (
        <PrepaidComponent
          prepaid={prepaidData.allocatedQuantity}
          usage={prepaidData.unit.balance}
          overageBalance={prepaidData.unit.overageBalance}
          name={prepaidData.name}
          unit={prepaidData.unit.name}
          description={prepaidData.description}
          isCumulative={prepaidData.pricePoint.rolloverPrepaidRemainder}
          cardVariant={cardVariant}
          isRenewable={prepaidData.pricePoint.renewPrepaidAllocation}
          translations={translations.prepaid}
        />
      );
    case 'on_off':
      const onOffData = data as OnOffComponentType;
      return (
        <OnOffComponent
          name={onOffData.name}
          isOn={onOffData.enabled}
          description={onOffData.description}
          isDisabled
        />
      );
    default:
      return <BodyText>Component not recognized: {type}</BodyText>;
  }
};

type ComponentContainerProps = SubscriptionComponent & {
  cardVariant: SubscriptionComponentCardVariants;
};

const ComponentContainer = ({
  componentMachineRef,
  cardVariant,
}: ComponentContainerProps) => {
  const { type, ...componentData } = useSelector(
    componentMachineRef,
    componentSelectors.getComponent
  );

  const { t } = useTranslation(['subscriptions-manager']);
  const translations = {
    prepaid: {
      overage_charge: t('subscription_components.prepaid.overage_charge'),
      resources_renew_every_billing_period: t(
        'subscription_components.prepaid.resources_renew_every_billing_period'
      ),
      remaining_resources_will_roll_over_to_next_billing_period: t(
        'subscription_components.prepaid.remaining_resources_will_roll_over_to_next_billing_period'
      ),
      overages: t('subscription_components.prepaid.overages'),
      usage: t('subscription_components.prepaid.usage'),
      prepaid: t('subscription_components.prepaid.prepaid'),
    },
  };

  return renderComponent(
    type,
    componentData as ComponentModel,
    cardVariant,
    translations
  );
};

export default ComponentContainer;
